/**
 * ZAPHUB 2.1 — ROI Dashboard
 *
 * Real-time view of hours and euros saved across all workflows.
 * Uses /api/ai-copilot/roi/:id for per-workflow estimates
 * and /api/admin/metrics for platform-wide stats.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  Clock,
  Euro,
  Zap,
  RefreshCw,
  Loader2,
  ChevronRight,
  BarChart3,
  Activity,
  Award,
} from 'lucide-react';
import api from '../services/apiClient';
import { useNavigate } from 'react-router-dom';

interface WorkflowROI {
  id: string;
  name: string;
  runCount: number;
  roi: {
    hoursPerMonth: number;
    costPerMonth: number;
    annualSavings: number;
    paybackPeriodDays: number;
  } | null;
  loading: boolean;
  error?: string;
}

interface PlatformStats {
  totalWorkflows: number;
  activeWorkflows: number;
  totalExecutions: number;
  successRate: number;
}

export function ROIDashboard() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<WorkflowROI[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [calculating, setCalculating] = useState(false);

  // Totals
  const totalHours = workflows.reduce((s, w) => s + (w.roi?.hoursPerMonth || 0), 0);
  const totalMonthlySavings = workflows.reduce((s, w) => s + (w.roi?.costPerMonth || 0), 0);
  const totalAnnualSavings = workflows.reduce((s, w) => s + (w.roi?.annualSavings || 0), 0);
  const completedCount = workflows.filter(w => w.roi && !w.loading).length;

  const loadWorkflows = useCallback(async () => {
    setLoadingList(true);
    try {
      const [wRes, statsRes] = await Promise.all([
        api.get('/workflows?limit=50&status=ACTIVE'),
        api.get('/admin/metrics').catch(() => ({ data: null })),
      ]);

      const list: WorkflowROI[] = (wRes.data.data?.workflows || wRes.data.data || []).map((w: any) => ({
        id: w.id,
        name: w.name,
        runCount: w.runCount || w.successCount || 0,
        roi: null,
        loading: false,
      }));

      setWorkflows(list);

      if (statsRes.data?.data) {
        const d = statsRes.data.data;
        setPlatformStats({
          totalWorkflows: d.workflows?.total || list.length,
          activeWorkflows: d.workflows?.active || 0,
          totalExecutions: d.executions?.total || 0,
          successRate: d.executions?.successRate || 0,
        });
      }
    } catch (err) {
      console.error('ROI Dashboard: failed to load workflows', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const calculateAllROI = async () => {
    if (calculating || workflows.length === 0) return;
    setCalculating(true);

    // Mark all as loading
    setWorkflows(prev => prev.map(w => ({ ...w, loading: true, roi: null, error: undefined })));

    // Calculate in parallel (batches of 3 to avoid rate limiting)
    const batchSize = 3;
    const results = [...workflows];

    for (let i = 0; i < results.length; i += batchSize) {
      const batch = results.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (w, batchIdx) => {
          const idx = i + batchIdx;
          try {
            const { data } = await api.post(`/ai-copilot/roi/${w.id}`, { hourlyRate });
            results[idx] = { ...results[idx], roi: data.data, loading: false };
          } catch {
            results[idx] = { ...results[idx], error: 'No data', loading: false };
          }
        })
      );
      setWorkflows([...results]);
    }

    setCalculating(false);
  };

  if (loadingList) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-10 h-10 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ROI Dashboard</h1>
          <p className="text-gray-500 mt-1">Impacto económico real de tu automatización</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">€/hora:</label>
            <input
              type="number"
              min={10}
              max={500}
              value={hourlyRate}
              onChange={e => setHourlyRate(Number(e.target.value))}
              className="w-20 px-2 py-1.5 border rounded-lg text-sm text-center"
            />
          </div>
          <Button
            onClick={calculateAllROI}
            disabled={calculating || workflows.length === 0}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            {calculating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculando...</>
            ) : (
              <><BarChart3 className="w-4 h-4 mr-2" /> Calcular todo</>
            )}
          </Button>
          <Button variant="outline" onClick={loadWorkflows}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Platform Stats */}
      {platformStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <Zap className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="text-2xl font-bold">{platformStats.totalWorkflows}</div>
            <div className="text-xs text-gray-500">Workflows totales</div>
          </Card>
          <Card className="p-4 text-center">
            <Activity className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="text-2xl font-bold">{platformStats.activeWorkflows}</div>
            <div className="text-xs text-gray-500">Activos</div>
          </Card>
          <Card className="p-4 text-center">
            <RefreshCw className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <div className="text-2xl font-bold">{platformStats.totalExecutions.toLocaleString()}</div>
            <div className="text-xs text-gray-500">Ejecuciones totales</div>
          </Card>
          <Card className="p-4 text-center">
            <Award className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="text-2xl font-bold">{platformStats.successRate.toFixed(1)}%</div>
            <div className="text-xs text-gray-500">Tasa de éxito</div>
          </Card>
        </div>
      )}

      {/* ROI Totals */}
      {completedCount > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-8 h-8 text-blue-600" />
              <div>
                <div className="text-3xl font-bold text-blue-800">{totalHours.toFixed(1)}h</div>
                <div className="text-sm text-blue-600">horas ahorradas/mes</div>
              </div>
            </div>
            <div className="text-xs text-blue-500">{completedCount} workflows analizados</div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <Euro className="w-8 h-8 text-green-600" />
              <div>
                <div className="text-3xl font-bold text-green-800">
                  €{totalMonthlySavings.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-sm text-green-600">ahorro mensual</div>
              </div>
            </div>
            <div className="text-xs text-green-500">
              a €{hourlyRate}/hora · {completedCount} workflows
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              <div>
                <div className="text-3xl font-bold text-purple-800">
                  €{totalAnnualSavings.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-sm text-purple-600">ahorro anual proyectado</div>
              </div>
            </div>
            <div className="text-xs text-purple-500">basado en ejecuciones actuales</div>
          </Card>
        </div>
      )}

      {/* Workflows Table */}
      <Card>
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-800">ROI por Workflow</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {workflows.length === 0
              ? 'No hay workflows activos'
              : completedCount === 0
              ? 'Haz clic en "Calcular todo" para ver el ROI de cada workflow'
              : `${completedCount}/${workflows.length} calculados`}
          </p>
        </div>

        <div className="divide-y">
          {workflows.map(w => (
            <div key={w.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
              {/* Name */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{w.name}</div>
                <div className="text-xs text-gray-500">{w.runCount} ejecuciones totales</div>
              </div>

              {/* ROI data */}
              {w.loading && (
                <div className="flex items-center gap-1 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  calculando...
                </div>
              )}

              {!w.loading && w.roi && (
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-blue-700">{w.roi.hoursPerMonth}h</div>
                    <div className="text-xs text-gray-400">horas/mes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-green-700">€{w.roi.costPerMonth.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-gray-400">ahorro/mes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-purple-700">€{w.roi.annualSavings.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-gray-400">anual</div>
                  </div>
                  <div className="text-center">
                    <Badge variant={w.roi.paybackPeriodDays <= 30 ? 'default' : 'secondary'} className="text-xs">
                      ROI en {w.roi.paybackPeriodDays}d
                    </Badge>
                  </div>
                </div>
              )}

              {!w.loading && !w.roi && w.error && (
                <span className="text-xs text-gray-400">{w.error}</span>
              )}

              {!w.loading && !w.roi && !w.error && (
                <span className="text-xs text-gray-400">pendiente</span>
              )}

              {/* Open */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/dashboard/workflows/${w.id}`)}
                className="flex-shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          ))}

          {workflows.length === 0 && (
            <div className="py-12 text-center text-gray-400">
              <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Crea workflows activos para ver su ROI aquí</p>
            </div>
          )}
        </div>
      </Card>

      <p className="text-xs text-gray-400 text-center">
        Las estimaciones de ROI son generadas por Claude AI basándose en la complejidad y frecuencia de ejecución.
        A €{hourlyRate}/hora de coste de mano de obra.
      </p>
    </div>
  );
}
